import { Circle, MagnifyingGlass, User } from "@phosphor-icons/react";
import React, { useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { Avatar, Badge, HideComponentWrapper, Typography } from "../../components";
import { IPopupSelect } from "../../models";
import { Popover } from "../Popover";
import styles from "./PopupSelect.module.css";

const selectStyles: any = {
	control: (provided: any) => ({
		...provided,
		minWidth: 240,
		margin: 0,
		border: "none",
		color: "inherit",
		boxShadow: "none",
		backgroundColor: "inherit"
	}),
	menu: () => ({ boxShadow: `inset 0 1px 0 var(--border-primary)` }),
	input: (provided: any) => ({
		...provided,
		marginLeft: "4px" // Adjust to create space between the icon and text
	}),
	placeholder: (provided: any) => ({
		...provided,
		marginLeft: "24px" // Align placeholder with input text
	}),
	dropdownIndicator: (provided: any) => ({
		...provided,
		color: "#6c757d"
	})
};

const PopupSelect: React.FC<IPopupSelect> = (props: IPopupSelect) => {
	const viewPopupRef = useRef(null);
	const handleSelectValue = (newValue: any) => {
		props.setValue(newValue);
		if (!props.isMulti) {
			(viewPopupRef?.current as any)?.closePopup();
		}
	};
	const CustomInput = (props: any) => (
		<div className={styles.inputWrapper}>
			<MagnifyingGlass size={"1.25rem"} className="icon-color" />
			<components.Input {...props} className={styles.custom_input_label} />
		</div>
	);
	const defaultOption = ({ data, innerProps }: { data: any; innerProps: any }) => {
		let isActiveClass = "";
		if (props.isMulti) {
			isActiveClass = props.value?.some((item: any) => item.value === data.value) ? styles.active : "";
		} else {
			isActiveClass = props.value?.value === data.value ? styles.active : "";
		}
		return (
			<div {...innerProps} aria-disabled={data.disabled} className={`${styles.custom_option}  ${isActiveClass}`}>
				<div className={styles.default_dropdown_item}>
					{props.type === "icon-leading" && <User size="1.25rem" className={` ${props.iconClass}`} />}
					{props.type === "dot-leading" && <Circle weight="fill" size={"0.625rem"} className={`${styles.icon_dot} ${props.iconClass}`} />}
					{props.type === "avatar-leading" && (
						<Avatar
							size="sm"
							src={data.src}
							src_text={data.src_text}
							id={data.value}
							image_hash={data.image_hash}
							mime_type={data.mime_type}
							alt={data.alt || data.label}
							apiRoutePrefix={data.apiRoutePrefix || `${window.location.origin}/api/v1/Auth/get_file`}
						/>
					)}
					<Typography size="md" fontFamily="regular" type="text" color="primary">
						{data.label}
					</Typography>
					<HideComponentWrapper show={!!data.supportingText}>
						<Typography size="sm" fontFamily="regular" type="text" color="tertiary">
							{data.supportingText}
						</Typography>
					</HideComponentWrapper>
					<HideComponentWrapper show={!!data.badge}>
						<Badge size={"sm"} type={"pill-color"} icon={null} color={"grey"}>
							{data.badge}
						</Badge>
					</HideComponentWrapper>
				</div>
				<i className={`ph ph-check ${styles.activecheck}`}></i>
			</div>
		);
	};
	const getCustomComponents = () => {
		let item: any = {
			IndicatorSeparator: () => null,
			Input: CustomInput,
			Option: defaultOption,
			DropdownIndicator: null
		};
		if (props.CustomOption) {
			item = {
				...item,
				Option: props.CustomOption
			};
		}
		return item;
	};
	return (
		<div style={props.formStyle} className={`${styles.popup_group} ${props.formClass ?? ""}`}>
			<HideComponentWrapper show={!!props.label}>
				<Typography className={`${styles.form_label} ${props.required === true ? styles.required : styles.optional}`}>
					{props.label}
				</Typography>
			</HideComponentWrapper>
			<Popover
				target={props.target}
				ref={props.ref || viewPopupRef}
				parentClass={`${styles.popup_select_cont} ${props.popupParentClass ?? "mt-1"}`}
				handlePopupVisible={props.handlePopupVisible}
				position={props.position || "bottom"}
				parentStyle={props.popupStyle}
				iconContainerClass={props.iconContainerClass}
				dropdownClass={props.dropdownClass}
			>
				<Select
					id={props.id}
					autoFocus
					backspaceRemovesValue={false}
					components={getCustomComponents()}
					controlShouldRenderValue={false}
					hideSelectedOptions={false}
					isClearable={false}
					menuIsOpen
					onChange={handleSelectValue}
					options={props.selectOptions}
					placeholder="Search..."
					styles={props.selectStyles || selectStyles}
					tabSelectsValue={false}
					value={props.value}
					isLoading={props.isLoading}
					isMulti={props.isMulti}
				/>
			</Popover>
		</div>
	);
};

PopupSelect.displayName = "PopupSelect";
export default PopupSelect;
